import { Suspense, useState } from 'react';

import { TAccount } from '@helpers/Accounts/accounts.types';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { tracking } from '@iwoca/frontend-tracking-library';
import { Button, Text } from '@iwoca/orion';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { RocketSVG } from '@svgComponents/rocketSVG/RocketSVG';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { useGetTopupApprovedBannerData } from './useGetTopupApprovedBannerData';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import { buildLocationWithSearch } from '../../../drawDown/DrawDownHelper';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import { useOfferFlowMemoryState } from '../../../myOffers/offerFlowMemory/useOfferFlowMemoryState';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { Rate } from '../../../ui/rate';
import { DrawdownActionPanel } from '../drawdownActionPanel/DrawdownActionPanel';

export const TopupApprovedBanner = ({
    accountId
}: {
    accountId: NonNullable<TAccount['account_id']>;
}) => {
    const [drawdownPanelIsOpen, setDrawdownPanelIsOpen] = useState(false);
    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <Suspense
                fallback={
                    <div data-testId="topup-approved-banner-loading">
                        <MainContentPending />
                    </div>
                }
            >
                <BannerContent
                    accountId={accountId}
                    setDrawdownPanelIsOpen={setDrawdownPanelIsOpen}
                />
                {drawdownPanelIsOpen && (
                    <div className="col-span-5 mt-[-36px]">
                        <DrawdownActionPanel
                            accountId={accountId}
                            setDrawdownPanelIsOpen={setDrawdownPanelIsOpen}
                        />
                    </div>
                )}
            </Suspense>
        </ErrorBoundary>
    );
};

const BannerContent = ({
    accountId,
    setDrawdownPanelIsOpen
}: {
    accountId: NonNullable<TAccount['account_id']>;
    setDrawdownPanelIsOpen: (isOpen: boolean) => void;
}) => {
    const {
        topUpApprovalTimeOut,
        approvalAmount,
        interestRate,
        isRBL,
        isRepaymentReduction,
        isPreApproved,
        applicationStatus
    } = useGetTopupApprovedBannerData(accountId);
    const { hasSavedProgress, isEnabled, getRestoreLocation, step, offerId } =
        useOfferFlowMemoryState();
    const history = useHistory();
    const isDE = getCountry() === 'DE';

    const canRestoreProgress = hasSavedProgress && isEnabled && isDE && !isRBL;

    const showRequestMoreFunds =
        getCountry() === 'UK' &&
        isPreApproved &&
        applicationStatus !== 'declined' &&
        !isRepaymentReduction;

    const handleRequestMoreFundsClick = () => {
        tracking.ampli.requestMoreFundsClicked({
            account_id: accountId,
            dashboard_type: 'dynamic'
        });
        history.push(
            maintainStateKey(
                // @ts-expect-error: buildLocationWithSearch is not typed
                buildLocationWithSearch({
                    pathname: '/request-topup/',
                    accountId
                })
            )
        );
    };

    const handleCtaClick = () => {
        tracking.ampli.topupApprovedBannerCtaClicked({
            account_id: accountId
        });

        if (isDE && !isRBL) {
            if (canRestoreProgress && offerId && step) {
                tracking.ampli.stepRestored({ offer_id: offerId, step });
            }
            const approvedPath = `/approved/${accountId}/`;
            history.push(
                maintainStateKey(
                    getRestoreLocation({
                        fallbackPathname: approvedPath,
                        returnPath: approvedPath
                    })
                )
            );
            return;
        }

        setDrawdownPanelIsOpen(true);
    };

    return (
        <>
            <div className="col-span-5 grid grid-flow-dense grid-cols-7 gap-m rounded-s border border-success-mid bg-success-lt p-l max-sm:col-span-full">
                <div className="col-span-1 m-auto max-sm:col-span-full">
                    <RocketSVG className="h-4xl w-4xl text-success-drk" />
                </div>
                <div className="col-span-6 max-sm:col-span-full max-sm:text-center">
                    <Text className="text-xl font-bold">
                        <BannerTitle
                            isRepaymentReduction={isRepaymentReduction}
                        />
                    </Text>
                    <Text className="text-l">
                        {isRepaymentReduction ? (
                            <FormattedMessage
                                id="_.topup_approved_banner.repayment_reudction_description"
                                defaultMessage="You can adjust your loan terms and reduce your mimimum monthly repayments."
                            />
                        ) : (
                            <FormattedMessage
                                id="_.topup_approved_banner.topup_approved_description"
                                defaultMessage="You can drawdown up to {amount} at a rate of {interestRate} per 30 days before {expiryDate}"
                                values={{
                                    amount: (
                                        <FormattedCurrency
                                            value={approvalAmount}
                                        />
                                    ),
                                    interestRate: (
                                        <Rate value={interestRate!} />
                                    ),
                                    expiryDate: (
                                        <FormattedDate
                                            value={
                                                new Date(topUpApprovalTimeOut!)
                                            }
                                            day="2-digit"
                                            month="short"
                                            year="numeric"
                                        />
                                    )
                                }}
                            />
                        )}
                    </Text>
                </div>
                <div className="col-span-6 col-start-2 flex flex-col items-center justify-center gap-m max-sm:col-span-full max-sm:text-center min-m:col-start-2 min-m:items-start">
                    <Button onClick={handleCtaClick}>
                        <CtaLabel
                            canRestoreProgress={canRestoreProgress}
                            isRepaymentReduction={isRepaymentReduction}
                        />
                    </Button>
                    {showRequestMoreFunds && (
                        <div className="flex flex-col items-center gap-2xs min-sm:flex-row min-sm:gap-s max-sm:text-center">
                            <Text className="text-s font-bold">
                                <FormattedMessage
                                    id="_.topup_approved_banner.need_more_than"
                                    defaultMessage="Need more than {amount}?"
                                    values={{
                                        amount: (
                                            <FormattedCurrency
                                                value={approvalAmount}
                                            />
                                        )
                                    }}
                                />
                            </Text>
                            <Button
                                variant="tertiary"
                                size="small"
                                onClick={handleRequestMoreFundsClick}
                            >
                                <FormattedMessage
                                    id="_.topup_approved_banner.request_more_funds.cta"
                                    defaultMessage="Request more funds"
                                />
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

const BannerTitle = ({
    isRepaymentReduction
}: {
    isRepaymentReduction: boolean;
}) => {
    if (isRepaymentReduction)
        return (
            <FormattedMessage
                id="_.topup_approved_banner.repayment_reduction_approved"
                defaultMessage="You can now reduce your minimum monthly repayments"
            />
        );

    return (
        <FormattedMessage
            id="_.topup_approved_banner.topup_approved_title"
            defaultMessage="You're approved!"
        />
    );
};

const CtaLabel = ({
    canRestoreProgress,
    isRepaymentReduction
}: {
    canRestoreProgress: boolean;
    isRepaymentReduction: boolean;
}) => {
    if (canRestoreProgress)
        return (
            <FormattedMessage
                id="_.topup_approved_banner.continue.cta"
                defaultMessage="Continue"
            />
        );

    if (isRepaymentReduction)
        return (
            <FormattedMessage
                id="_.topup_approved_banner.adjust_terms.cta"
                defaultMessage="Adjust your terms"
            />
        );

    return (
        <FormattedMessage
            id="_.topup_approved_banner.choose_drawdown_amount.cta"
            defaultMessage="Access funds"
        />
    );
};
